import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  IconButton,
  Typography,
  Divider,
  Avatar,
  Tooltip
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import Farmer360logo from '../../assets/Farmer360logo.png';

const drawerWidth = 220;

const navItems = [
  {label:'DashBoard', icon:<AutorenewIcon />, path:'/SOorMDO/dashboard'},
  { label: 'Farmer Commitment', icon: <HomeFilledIcon />, path: '/SOorMDO/farmerCommitment' },
  { label: 'Farmer Reference Lead', icon: <HowToRegIcon />, path: '/SOorMDO/farmerReferenceLead' },
  { label: 'Pre Activity', icon: <ListAltIcon />, path: '/SOorMDO/preActivity' },
  { label: 'Post Activity', icon: <AssignmentTurnedInIcon />, path: '/SOorMDO/postActivity' },
  { label: 'Sales Target', icon: <TrendingUpIcon />, path: '/SOorMDO/salesTarget' },
  {label:'Daily Activity', icon:<ListAltIcon />, path:'/SOorMDO/travelPunchingLogList'},
  { label: 'Daily Task', icon: <AssignmentTurnedInIcon />, path: '/SOorMDO/dailyTask' },
 
];

// Compact mobile nav items (xs): three icons row per the design
const mobileItems = [
  { label: 'Commitment', icon: <HomeFilledIcon />, path: '/SOorMDO/farmerCommitment' },
  { label: 'Lead', icon: <HowToRegIcon />, path: '/SOorMDO/farmerReferenceLead' },
  { label: 'Activities', icon: <AutorenewIcon />, path: '/SOorMDO/activities' },
  { label: 'Tasks', icon: <TaskAltIcon />, path: '/SOorMDO/tasks' },
];

function Sidebar() {
    // ...existing code...
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    const idx = navItems.findIndex((item) => {
      // Check main path or related paths
      if (location.pathname.startsWith(item.path)) return true;
      if (item.related && Array.isArray(item.related)) {
        return item.related.some(rel => location.pathname === rel || location.pathname.startsWith(rel));
      }
      return false;
    });
    setSelectedIndex(idx === -1 ? null : idx);
  }, [location.pathname]);

  
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('userDetails'));
    if (storedUser?.name) {
      setUserName(storedUser.name);
    }
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleLogoClick = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    if (!userDetails?.token) {
      // User not logged in, navigate to home
      navigate('/');
    } else {
      // User logged in, navigate to appropriate dashboard based on role
      if (userDetails.role === 'ADMIN' || userDetails.role === 'ZM' || userDetails.role === 'AE') {
        navigate('/masterData/dashboard');
      } else if (userDetails.role === 'RM' || userDetails.role === 'AM') {
        navigate('/amrm');
      } else if (userDetails.role === 'SO' || userDetails.role === 'MDO') {
        navigate('/SOorMDO/dashboard');
      } else {
        navigate('/');
      }
    }
  };

  const handleItemClick = (index) => {
    setSelectedIndex(index);
    const item = navItems[index];
    if (item.path) {
      navigate(item.path);
    }
    if (isSm) setMobileOpen(false);
  };

  const drawerContent = (
    <Box role="presentation" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          mt: 1,
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.8
          }
        }}
        onClick={handleLogoClick}
      >
        <img src={Farmer360logo} alt="Farmer360" style={{ width: 150, height: 80, objectFit: 'contain' }} />
      </Box>
      <Divider />
      <List sx={{ px: 1 }}>
        {navItems.map((item, index) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(index)}
              selected={selectedIndex === index}
              sx={{
                borderRadius: 1,
                position: 'relative',
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-selected .MuiListItemText-primary': {
                  color: '#4318FF',
                },
                '&.Mui-selected .MuiSvgIcon-root': {
                  color: '#4318FF',
                },
                '&.Mui-selected::after': {
                  content: '""',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '60%',
                  borderRadius: '3px',
                  backgroundColor: '#4318FF',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 20, '& .MuiSvgIcon-root': { width: 20.4, height: 18.6 } }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                slotProps={{
                  primary: {
                    sx: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', ml: 1.5 },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      {/* Profile icon at the bottom */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center',  gap: 1 }}>
        <Tooltip title="Profile">
          <IconButton onClick={() => navigate('/profile')} size="large">
            <Avatar sx={{ bgcolor: '#4318FF', width: 40, height: 40 }}>
              <AccountCircleIcon sx={{ width: 32, height: 32 }} />
            </Avatar>
          </IconButton>
        </Tooltip>
        {userName && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'black',
              maxWidth: 140,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </Typography>
        )}
      </Box>
    </Box>
  );
  const totalTabs = mobileItems.length ; // +1 for Profile tab
  // Show xs top NavBar for SOorMDO paths only
  const showXsNavBar = isXs && location.pathname.startsWith('/SOorMDO');
  return (
    <Box sx={{ display: 'flex' }}>
      {/* xs: top NavBar with logo and profile, no login, for SOorMDO only */}
      {showXsNavBar && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={2}
          sx={{
            display: { xs: 'flex', sm: 'none', md: 'none' },
            top: 0,
            left: 0,
            right: 0,
            bgcolor: '#FFFFFF',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            zIndex: (theme) => theme.zIndex.drawer + 2,
            py: 0.5,
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 56, px: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={handleLogoClick}>
              <img src={Farmer360logo} alt="Farmer360" style={{ width: 110, height: 40, objectFit: 'contain' }} />
            </Box>
            {(() => {
              const isProfileSelected = location.pathname.startsWith('/profile');
              return (
                <Box
                  key="Profile"
                  onClick={() => navigate('/profile')}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    cursor: 'pointer',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isProfileSelected ? '#4318FF' : 'rgba(0,0,0,0.35)',
                  }}
                  aria-label="Profile"
                  role="button"
                >
                  <Avatar sx={{ bgcolor: '#4318FF', width: 28, height: 28 }}>
                    <AccountCircleIcon sx={{ width: 22, height: 22 }} />
                  </Avatar>
                  <Typography sx={{ mt: 0.5, fontSize: 12 }}>Profile</Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      height: 3,
                      width: isProfileSelected ? 28 : 0,
                      bgcolor: '#4318FF',
                      borderRadius: 2,
                      transition: 'width 200ms ease',
                    }}
                  />
                </Box>
              );
            })()}
          </Toolbar>
        </AppBar>
      )}
      {/* xs: bottom icon navigation per design */}
      {isXs && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={2}
          sx={{
            display: { xs: 'block', sm: 'none', md: 'none' },
            top: 'auto',
            bottom: 0,
            bgcolor: '#FFFFFF',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 56, px: 2 }}>
           <Box
  sx={{
    display: 'grid',
    gridTemplateColumns: `repeat(${totalTabs}, 1fr)`,
    width: '100%',
    gap: 0.5,
  }}
>
              {mobileItems.map((item) => {
                const isSelected =
                  (item.path === '/SOorMDO/farmerCommitment' && location.pathname.startsWith('/SOorMDO/farmerCommitment')) ||
                  (item.path === '/SOorMDO/farmerReferenceLead' && location.pathname.startsWith('/SOorMDO/farmerReferenceLead')) ||
                  (item.path === '/SOorMDO/activities' && (
                    location.pathname.startsWith('/SOorMDO/activities') ||
                    location.pathname.startsWith('/SOorMDO/preActivity') ||
                    location.pathname.startsWith('/SOorMDO/postActivity') ||
                    location.pathname.startsWith('/SOorMDO/salesTarget')
                  )) ||
                  (item.path === '/SOorMDO/tasks' && (
                    location.pathname.startsWith('/SOorMDO/tasks') ||
                    location.pathname.startsWith('/SOorMDO/travelPunchingLogList') ||
                    location.pathname.startsWith('/SOorMDO/dailyTask')
                  ));

                return (
                  <Box
                    key={item.label}
                    onClick={() => navigate(item.path)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isSelected ? '#4318FF' : 'rgba(0,0,0,0.35)',
                    }}
                    aria-label={item.label}
                    role="button"
                  >
                    <Box sx={{ '& .MuiSvgIcon-root': { width: 24, height: 24, color: 'currentColor' } }}>{item.icon}</Box>
                    <Typography sx={{ mt: 0.5, fontSize: 12 }}>{item.label}</Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        height: 3,
                        width: isSelected ? 28 : 0,
                        bgcolor: '#4318FF',
                        borderRadius: 2,
                        transition: 'width 200ms ease',
                      }}
                    />
                  </Box>
                );
              })}
              {/* Profile icon/tab for xs screen */}
              {/* {(() => {
                const isProfileSelected = location.pathname.startsWith('/profile');
                return (
                  <Box
                    key="Profile"
                    onClick={() => navigate('/profile')}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isProfileSelected ? '#4318FF' : 'rgba(0,0,0,0.35)',
                    }}
                    aria-label="Profile"
                    role="button"
                  >
                    <Avatar sx={{ bgcolor: '#4318FF', width: 28, height: 28 }}>
                      <AccountCircleIcon sx={{ width: 22, height: 22 }} />
                    </Avatar>
                    <Typography sx={{ mt: 0.5, fontSize: 12 }}>Profile</Typography>
                    <Box
                      sx={{
                        mt: 0.5,
                        height: 3,
                        width: isProfileSelected ? 28 : 0,
                        bgcolor: '#4318FF',
                        borderRadius: 2,
                        transition: 'width 200ms ease',
                      }}
                    />
                  </Box>
                );
              })()} */}
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* sm: original top bar with menu icon */}
      {isSm && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{ display: { xs: 'none', sm: 'block', md: 'none' }, borderBottom: 0.5, borderColor: 'transparent' }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 56 }}>
            <IconButton edge="start" aria-label="open menu" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              Menu
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Box component="nav" aria-label="sidebar navigation" sx={{ flexShrink: { md: 0 } }}>
        {/* sm: original temporary drawer with menu toggle */}
        {isSm && (
          <Drawer
            variant={'temporary'}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                backgroundColor: '#FFFFFF',
                boxSizing: 'border-box',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}

        {/* md and up: permanent drawer exactly as before */}
        {isMdUp && (
          <Drawer
            variant={'permanent'}
            open={true}
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                backgroundColor: '#FFFFFF',
                boxSizing: 'border-box',
              },
            }}
          >
            {drawerContent}
          </Drawer>
        )}
      </Box>
    </Box>
  );
}

export default Sidebar;
